import { useEffect, useState } from "react";
import { FileText, Upload, Loader2, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Spinner, ErrorNote } from "@/components/PortalPage";
import { api } from "@/lib/api";
import ReviewTermsDialog from "@/components/agreements/ReviewTermsDialog";
import { openAgreementPdf, uploadAgreement, tenureText } from "@/components/agreements/agreementApi";

const LEVEL = { high: "#FF6B6B", medium: "#F5B700" };
const STATUS = { confirmed: ["Confirmed", "#4CCC06"], draft: ["Needs review", "#F5B700"] };

const Row = ({ r, onUpload, onReview, busy }) => {
  const [label, color] = r.agreement ? STATUS[r.agreement.status] || STATUS.draft : ["Not uploaded", "#FF6B6B"];
  return (
    <div data-testid={`agreement-row-${r.user_id}`} className="rounded-xl border border-[#333] bg-[#1A1A1A] p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-bold text-white">{r.full_name || r.email}</p>
          <p className="text-xs text-neutral-400">{r.tenure ? `${tenureText(r.tenure)} with ALR · since ${r.tenure.start_date}` : "Start date unknown"}{r.agreement?.terms?.territory ? ` · ${r.agreement.terms.territory}` : ""}</p>
        </div>
        <span data-testid={`agreement-status-${r.user_id}`} className="rounded-full border px-2.5 py-0.5 text-[11px] font-extrabold" style={{ color, borderColor: color }}>{label}</span>
        {r.agreement && <button data-testid={`agreement-view-${r.user_id}`} onClick={() => openAgreementPdf(`admin/${r.user_id}/file`, r.agreement.filename).catch((e) => toast.error(e.message))} className="flex items-center gap-1 rounded-lg border border-[#333] px-3 py-1.5 text-xs font-bold text-white"><FileText size={13} />PDF</button>}
        {r.agreement && <button data-testid={`agreement-review-${r.user_id}`} onClick={() => onReview(r)} className="flex items-center gap-1 rounded-lg border border-[#4CCC06] px-3 py-1.5 text-xs font-bold text-[#4CCC06]"><ClipboardCheck size={13} />{r.agreement.status === "confirmed" ? "Edit terms" : "Review terms"}</button>}
        <label data-testid={`agreement-upload-${r.user_id}`} className="flex cursor-pointer items-center gap-1 rounded-lg bg-[#4CCC06] px-3 py-1.5 text-xs font-extrabold text-black">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}{busy ? "Reading…" : r.agreement ? "Replace" : "Upload PDF"}
          <input type="file" accept="application/pdf" className="hidden" disabled={busy} onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) onUpload(r, f); }} />
        </label>
      </div>
      {r.flags.length > 0 && (
        <ul className="mt-3 flex flex-col gap-1" data-testid={`agreement-flags-${r.user_id}`}>
          {r.flags.map((f, i) => <li key={i} className="flex items-center gap-2 text-xs" style={{ color: LEVEL[f.level] }}><span className="h-1.5 w-1.5 rounded-full" style={{ background: LEVEL[f.level] }} />{f.text}</li>)}
        </ul>
      )}
    </div>
  );
};

export default function Agreements() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [review, setReview] = useState(null);
  const load = () => api("/agreements/admin/list").then(setData).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);
  const upload = async (r, file) => {
    setBusyId(r.user_id);
    try {
      const ag = await uploadAgreement(r.user_id, file);
      toast.success("Agreement uploaded. Check the terms AI found.");
      await load();
      setReview({ ...r, agreement: ag });
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };
  return (
    <PortalPage eyebrow="CORPORATE ADMIN" title="Franchise Agreements" testId="agreements-page" width="max-w-5xl" back="/admin" backLabel="Admin">
      <p className="text-sm text-neutral-400">Upload each location's signed agreement. AI reads the key terms, you confirm them, and the portal flags anything out of line. Flags are only visible to admins. Franchisees can view and download their own copy.</p>
      <ErrorNote>{err}</ErrorNote>
      {!data ? <Spinner /> : (
        <>
          <p data-testid="agreements-summary" className="text-sm text-white"><b>{data.rows.length}</b> locations · <b style={{ color: "#FF6B6B" }}>{data.missing}</b> missing an agreement · <b style={{ color: "#F5B700" }}>{data.flagged}</b> with flags</p>
          <div className="flex flex-col gap-3">{data.rows.map((r) => <Row key={r.user_id} r={r} onUpload={upload} onReview={setReview} busy={busyId === r.user_id} />)}</div>
        </>
      )}
      {review && <ReviewTermsDialog row={review} onClose={() => setReview(null)} onSaved={load} />}
    </PortalPage>
  );
}
